import { useEffect, useState } from 'react';
import { ApiError, get } from './api';

/**
 * Busy hours on the day board (owner 11/1, from the draft he approved): a "Busy hours" switch on the right of the day buttons, Customers
 * or Money, and what to base it on (last 5 of a weekday, every one of a weekday in the last year, the same day last year, or a
 * date). When it's on, a strip above Staff needed shows each hour's average with a coloured bar, the busiest hour outlined, and
 * the busiest hours shaded in the hour row. Each manager's choice is remembered on their own computer. Never printed.
 * Activity (owner 11/2: "how busy the employees are with hands on duties"): each hour's races, laser tag games, movie showings and
 * other attraction sessions that had people in them, plus the sales rung up; the hover note breaks it down with the people in each.
 */
export type Measure = 'cust' | 'money' | 'activity';
type Words = [string, string, string];
export interface ActivityData {
  ready: boolean; pending: number; problem: string | null; days: number;
  hours: { H: number; kinds: Record<string, [number, number]>; sessions: number }[]; words: Record<string, Words>;
}
type BasisKind = 'last5' | 'year' | 'lastyear' | 'date' | 'holiday';
export interface BusyData {
  label: string; ready: boolean; problem: string | null; read: number; closed: number;
  hours: { H: number; n: number; amt: number }[]; used: { day: string; n: number; amt: number }[]; avgDay: { n: number; amt: number };
  activity: ActivityData | null;
}
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const isoAdd = (iso: string, n: number) => { const d = new Date(iso + 'T12:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
const mdy = (iso: string) => `${Number(iso.slice(5, 7))}/${Number(iso.slice(8, 10))}/${iso.slice(0, 4)}`;
const dowOf = (iso: string) => new Date(iso + 'T12:00:00Z').getUTCDay();
/** Holidays to compare with (owner 11/1): federal holidays plus Black Friday, Christmas Eve, New Year's Eve and New Year's Day,
 *  on the day itself. The server has the same list (server/src/busyHours.ts). */
const HOLIDAYS: [string, string][] = [
  ['newyear', 'New Year’s Day'], ['mlk', 'Martin Luther King Jr. Day'], ['presidents', 'Presidents’ Day'], ['memorial', 'Memorial Day'],
  ['juneteenth', 'Juneteenth'], ['july4', 'Independence Day (July 4)'], ['labor', 'Labor Day'], ['columbus', 'Columbus / Indigenous Peoples’ Day'],
  ['veterans', 'Veterans Day'], ['thanksgiving', 'Thanksgiving'], ['blackfriday', 'Black Friday'], ['xmaseve', 'Christmas Eve'],
  ['xmas', 'Christmas Day'], ['nye', 'New Year’s Eve'],
];
const iso = (y: number, m: number, d: number) => `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
const nth = (y: number, m: number, wd: number, n: number) => {
  if (n < 0) { const last = new Date(Date.UTC(y, m, 0)); return iso(y, m, last.getUTCDate() - ((last.getUTCDay() - wd + 7) % 7)); }
  const first = new Date(Date.UTC(y, m - 1, 1)).getUTCDay();
  return iso(y, m, 1 + ((wd - first + 7) % 7) + 7 * (n - 1));
};
const HDATE: Record<string, (y: number) => string> = {
  newyear: (y) => iso(y, 1, 1), mlk: (y) => nth(y, 1, 1, 3), presidents: (y) => nth(y, 2, 1, 3), memorial: (y) => nth(y, 5, 1, -1),
  juneteenth: (y) => iso(y, 6, 19), july4: (y) => iso(y, 7, 4), labor: (y) => nth(y, 9, 1, 1), columbus: (y) => nth(y, 10, 1, 2),
  veterans: (y) => iso(y, 11, 11), thanksgiving: (y) => nth(y, 11, 4, 4), blackfriday: (y) => isoAdd(nth(y, 11, 4, 4), 1),
  xmaseve: (y) => iso(y, 12, 24), xmas: (y) => iso(y, 12, 25), nye: (y) => iso(y, 12, 31),
};
/** The holiday the board's day is, or else the next one after it (what a manager is most likely planning for). */
function holidayFor(day: string): string {
  const y = +day.slice(0, 4);
  const exact = HOLIDAYS.find(([k]) => HDATE[k]!(y) === day);
  if (exact) return exact[0];
  const next = HOLIDAYS.map(([k]) => { const d = HDATE[k]!(y); return [k, d >= day ? d : HDATE[k]!(y + 1)] as const; }).sort((a, b) => a[1].localeCompare(b[1]))[0]!;
  return next[0];
}
export const isHoliday = (day: string) => HOLIDAYS.some(([k]) => HDATE[k]!(+day.slice(0, 4)) === day);

const store = { get: (k: string) => { try { return localStorage.getItem(k); } catch { return null; } }, set: (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* fine */ } } };

export function useBusy(locationId: number, date: string) {
  const [on, setOn0] = useState(() => store.get('mw.busy.on') === '1');
  const [measure, setMeasure0] = useState<Measure>(() => { const m = store.get('mw.busy.measure'); return m === 'money' || m === 'activity' ? m : 'cust'; });
  const [kind, setKind] = useState<BasisKind>('last5');
  const [weekday, setWeekday] = useState<number | null>(null);   // null = the board's day
  const [on2, setOn2] = useState('');                            // a chosen date
  const [holiday, setHoliday] = useState(() => holidayFor(date));
  const [years, setYears] = useState(3);
  const [data, setData] = useState<BusyData | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const setOn = (v: boolean) => { setOn0(v); store.set('mw.busy.on', v ? '1' : '0'); };
  const setMeasure = (v: Measure) => { setMeasure0(v); store.set('mw.busy.measure', v); };
  const [tick, setTick] = useState(0);                           // asks again while race times are still being read
  const wd = weekday ?? dowOf(date);
  const act = measure === 'activity';
  const lastYear = isoAdd(date, -364);   // the same weekday a year back
  // a new day: its own weekday, and its holiday (or the next one); on a holiday the strip starts on that holiday
  useEffect(() => { setWeekday(null); setHoliday(holidayFor(date)); if (isHoliday(date)) setKind('holiday'); }, [date]);
  useEffect(() => {
    if (!on) return;
    if (kind === 'date' && !on2) { setData(null); return; }
    const q = kind === 'holiday' ? `basis=holiday&holiday=${holiday}&years=${years}`
      : kind === 'date' || kind === 'lastyear' ? `basis=date&on=${kind === 'date' ? on2 : lastYear}` : `basis=${kind}&weekday=${wd}`;
    let gone = false;
    setLoading(true);
    get<BusyData>(`/api/schedule/busy?location=${locationId}&date=${date}&${q}${act ? '&act=1' : ''}`)
      .then((x) => { if (!gone) { setData(x); setErr(''); if (x.activity?.pending) setTimeout(() => { if (!gone) setTick((t) => t + 1); }, 1500); } })
      .catch((e) => { if (!gone) { setData(null); setErr(e instanceof ApiError ? e.message : 'The busy hours couldn’t be loaded.'); } })
      .finally(() => { if (!gone) setLoading(false); });
    return () => { gone = true; };
  }, [on, locationId, date, kind, wd, on2, lastYear, holiday, years, act, tick]);
  return { on, setOn, measure, setMeasure, kind, setKind, wd, setWeekday, on2, setOn2, lastYear, holiday, setHoliday, years, setYears, data, loading, err, date };
}
export type Busy = ReturnType<typeof useBusy>;

/** The value shown for an hour, and the busiest hours (top fifth of the day's busiest hour). */
export function busyView(b: Busy) {
  const actOf = (H: number) => b.data?.activity?.hours.find((y) => y.H === H);
  const val = (x: { H: number; n: number; amt: number }) => (b.measure === 'money' ? x.amt : b.measure === 'activity' ? (b.data?.activity ? Math.round((actOf(x.H)?.sessions ?? 0) + x.n) : 0) : x.n);
  const hrs = b.data?.hours ?? [];
  const max = Math.max(0, ...hrs.map(val));
  const peak = max > 0 ? hrs.find((x) => val(x) === max)!.H : null;
  return { val, max, peak, actOf, hot: (H: number) => { const x = hrs.find((y) => y.H === H); return !!(b.on && x && max > 0 && val(x) / max > 0.8); } };
}
/** "3 races (14 drivers), 1 laser tag game (8 players), 12 sales rung up" (averages, so 2.4 races is possible). */
function breakdown(a: ActivityData['hours'][number] | undefined, words: Record<string, Words>, sales: number): string {
  const one = (v: number) => (Math.round(v * 10) / 10).toString();
  const parts = Object.entries(a?.kinds ?? {}).filter(([, [n]]) => n > 0).map(([k, [n, p]]) => { const w = words[k] ?? ['session', 'sessions', 'people']; return `${one(n)} ${n === 1 ? w[0] : w[1]} (${one(p)} ${w[2]})`; });
  parts.push(`${sales} ${sales === 1 ? 'sale' : 'sales'} rung up`);
  return parts.join(', ');
}
const MEASURE_WORDS: Record<Measure, string> = { cust: 'customers', money: 'money taken', activity: 'activity' };
const fmt = (m: Measure, v: number) => (m === 'money' ? (v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${v}`) : String(v));
const hourWords = (H: number) => { const h = H % 24; return `${h % 12 === 0 ? 12 : h % 12} ${h >= 12 ? 'PM' : 'AM'}`; };

/** The switch and its choices (next to Modify presets). */
export function BusyControls({ b }: { b: Busy }) {
  const opt = (k: BasisKind, label: string) => <option value={k}>{label}</option>;
  return (
    <span className={'busyctl' + (b.on ? ' on' : '')}>
      <label className="busysw"><input type="checkbox" checked={b.on} onChange={(e) => b.setOn(e.target.checked)} /><span className="busytrack" aria-hidden="true"><span /></span>Busy hours</label>
      {b.on && <>
        <span className="busyseg" role="group" aria-label="Busy by">
          <button className={b.measure === 'cust' ? 'on' : ''} aria-pressed={b.measure === 'cust'} onClick={() => b.setMeasure('cust')}>Customers</button>
          <button className={b.measure === 'money' ? 'on' : ''} aria-pressed={b.measure === 'money'} onClick={() => b.setMeasure('money')}>Money</button>
          <button className={b.measure === 'activity' ? 'on' : ''} aria-pressed={b.measure === 'activity'} onClick={() => b.setMeasure('activity')}
            title="Races, laser tag games, movie showings and other attraction sessions with people in them, plus sales rung up">Activity</button>
        </span>
        <select className="busysel" aria-label="Based on" value={b.kind} onChange={(e) => b.setKind(e.target.value as BasisKind)}>
          {opt('last5', `Last 5 ${DAYS[b.wd]}s`)}
          {opt('year', `Every ${DAYS[b.wd]}, 12 months`)}
          {opt('lastyear', `Same day last year (${mdy(b.lastYear)})`)}
          {opt('holiday', 'A holiday…')}
          {opt('date', 'A date…')}
        </select>
        {b.kind === 'holiday' && <>
          <select className="busysel" aria-label="Holiday" value={b.holiday} onChange={(e) => b.setHoliday(e.target.value)}>
            {HOLIDAYS.map(([k, n]) => <option key={k} value={k}>{n}</option>)}
          </select>
          <select className="busysel" aria-label="Years to compare" value={b.years} onChange={(e) => b.setYears(Number(e.target.value))}>
            <option value={1}>last year</option><option value={2}>2-year avg</option><option value={3}>3-year avg</option>
          </select>
        </>}
        {(b.kind === 'last5' || b.kind === 'year') && (
          <select className="busysel" aria-label="Day of the week" value={b.wd} onChange={(e) => b.setWeekday(Number(e.target.value))}>
            {DAYS.map((d, i) => <option key={d} value={i}>{d.slice(0, 3)}</option>)}
          </select>
        )}
        {b.kind === 'date' && <input className="busysel" type="date" aria-label="Date to look at" value={b.on2} max={isoAdd(new Date().toISOString().slice(0, 10), -1)} onChange={(e) => b.setOn2(e.target.value)} />}
      </>}
    </span>
  );
}

/** The strip: one bar per hour, lined up with the day board's hour columns (each hour = 4 quarter columns). */
export function BusyStrip({ b, gcls, hours }: { b: Busy; gcls: string; hours: number[] }) {
  if (!b.on) return null;
  const v = busyView(b), d = b.data;
  const sub = b.kind === 'date' && !b.on2 ? 'pick a date' : d ? d.label : b.loading ? 'reading…' : '';
  const a = b.measure === 'activity' ? d?.activity : null;
  const actSessions = a ? Math.round(a.hours.reduce((t, x) => t + x.sessions, 0)) : 0;
  // owner 11/2 saw "0 attraction sessions": the Customers numbers were still up while Activity was being read
  const actWaiting = b.measure === 'activity' && !!d && !d.activity;
  const actNote = actWaiting ? 'Reading attraction times…' : !a ? '' : a.pending ? `Reading attraction times… (${a.pending} ${a.pending === 1 ? 'day' : 'days'} to go)` : a.problem
    ?? (d && d.used.length && !actSessions ? (a.days ? 'No races, games or showings found on those days, so this is the same as Customers.' : 'No attraction times read for those days yet.') : '');
  const note = b.err || d?.problem || actNote || (d && d.used.length === 0 && !b.loading ? (d.read ? 'No sales on those days (closed?).' : 'No sales read for those days yet.') : '');
  return (
    <div className={gcls + ' noprint busyrow'}>
      <div className="stl alt busylbl"><b>Busy hours</b><span>{MEASURE_WORDS[b.measure]}{sub ? ` · ${sub}` : ''}</span></div>
      {hours.map((H) => {
        const x = d?.hours.find((y) => y.H === H);
        const val = x ? v.val(x) : 0, f = v.max ? val / v.max : 0;
        const color = f > 0.8 ? '#C0392B' : f > 0.55 ? '#E67E22' : f > 0.3 ? '#F1C40F' : '#9ACD8A';
        return (
          <div key={H} className={'busycell' + (H === v.peak ? ' peak' : '')} title={!x ? '' : b.measure === 'activity' ? `${hourWords(H)}: about ${val} things on average, ${d!.label}: ${breakdown(v.actOf(H), a?.words ?? {}, x.n)}`
            : `${hourWords(H)}: ${fmt(b.measure, val)} on average (${b.measure === 'money' ? 'money taken' : 'sales rung up'}), ${d!.label}`}>
            {val > 0 && <><div className="busybar" style={{ height: Math.max(3, f * 38), background: color }} /><span className="busynum">{fmt(b.measure, val)}</span></>}
          </div>
        );
      })}
      <div className="busyend">
        {note ? <span style={{ color: '#7A4A06' }}>{note}</span>
          : d && d.used.length ? <>Avg day {b.measure === 'money' ? fmt('money', d.avgDay.amt) : b.measure === 'activity' ? `${d.hours.reduce((t, x) => t + v.val(x), 0)} things (${actSessions} attraction sessions + ${d.avgDay.n} sales)` : `${d.avgDay.n} customers`}{v.peak != null && <><br />Busiest {hourWords(v.peak).replace(' ', '')}–{hourWords(v.peak + 1).replace(' ', '')}</>}
            <br /><span title={d.used.map((u) => `${mdy(u.day)}: ${b.measure === 'money' ? fmt('money', u.amt) : u.n}`).join('\n')}>{d.used.length} {d.used.length === 1 ? 'day' : 'days'}{d.closed ? ` (${d.closed} closed left out)` : ''}</span></> : null}
      </div>
    </div>
  );
}
